import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { Textarea } from './ui/textarea';
import { Upload, Settings, FileText, Key, Database, Sparkles, BarChart3, Brain, Palette, Trash2, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPanel = () => {
  const [settings, setSettings] = useState({
    openrouter_api_key: '',
    openrouter_api_key_configured: false,
    openrouter_api_key_from_env: false,
    openrouter_model: '~anthropic/claude-haiku-latest'
  });
  const [documents, setDocuments] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [embeddingSettings, setEmbeddingSettings] = useState({
    openai_api_key: '',
    use_embeddings: true
  });
  const [migrating, setMigrating] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [evalSettings, setEvalSettings] = useState({
    llm_evaluation_enabled: true,
    llm_evaluation_sample_rate: 0.10,
    llm_evaluator_model: '~anthropic/claude-haiku-latest',
    llm_evaluation_threshold_safety: 0.7,
    llm_evaluation_threshold_general: 0.5
  });
  const [savingEvalSettings, setSavingEvalSettings] = useState(false);
  const [personaConfig, setPersonaConfig] = useState({
    active_style: 'warm',
    custom_principles: ''
  });
  const [savingPersona, setSavingPersona] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [savingSystemPrompt, setSavingSystemPrompt] = useState(false);

  const personaStyleOptions = [
    { value: 'warm', label: 'Warm', description: 'Supportive, encouraging, professional — the default tone' },
    { value: 'clinical', label: 'Clinical', description: 'Evidence-based, precise, references developmental norms' },
    { value: 'casual', label: 'Casual', description: 'Friendly, relatable — like a knowledgeable friend' },
    { value: 'custom', label: 'Custom', description: 'Write your own personality guidelines' }
  ];

  // Only the OpenRouter "latest" aliases: they follow Anthropic's current
  // release, so a discontinued version can never break chat.
  const modelOptions = [
    { value: '~anthropic/claude-sonnet-latest', label: 'Claude Sonnet (latest)' },
    { value: '~anthropic/claude-haiku-latest', label: 'Claude Haiku (latest)' }
  ];

  useEffect(() => {
    fetchSettings();
    fetchDocuments();
    fetchEvaluationSettings();
    fetchPersonaConfig();
    fetchSystemPrompt();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/settings`);
      setSettings({
        openrouter_api_key: '',
        openrouter_api_key_configured: response.data.openrouter_api_key_configured || false,
        openrouter_api_key_from_env: response.data.openrouter_api_key_from_env || false,
        openrouter_model: response.data.openrouter_model
      });
      
      // Fetch embedding settings if available
      if (response.data.openai_api_key_configured !== undefined) {
        setEmbeddingSettings({
          openai_api_key: response.data.openai_api_key || '',
          use_embeddings: response.data.use_embeddings !== false // default true
        });
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchEvaluationSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/settings/evaluation`);
      setEvalSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch evaluation settings:', error);
    }
  };

  const fetchPersonaConfig = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/settings/persona`);
      setPersonaConfig({
        active_style: response.data.active_style || 'warm',
        custom_principles: response.data.custom_principles || ''
      });
    } catch (error) {
      console.error('Failed to fetch persona config:', error);
    }
  };

  const fetchSystemPrompt = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/settings/system-prompt`);
      setSystemPrompt(response.data.system_prompt || '');
    } catch (error) {
      console.error('Failed to fetch system prompt:', error);
    }
  };

  const handleSystemPromptSave = async () => {
    if (!systemPrompt.trim()) {
      toast.error('System prompt cannot be empty');
      return;
    }
    setSavingSystemPrompt(true);
    try {
      await axios.put(`${API_BASE}/admin/settings/system-prompt`, {
        system_prompt: systemPrompt
      });
      toast.success('System prompt updated successfully');
    } catch (error) {
      const msg = error.response?.data?.detail;
      toast.error(msg || 'Failed to update system prompt');
    } finally {
      setSavingSystemPrompt(false);
    }
  };

  const handlePersonaSave = async () => {
    setSavingPersona(true);
    try {
      const payload = {
        active_style: personaConfig.active_style,
        custom_principles: personaConfig.active_style === 'custom' ? personaConfig.custom_principles : null
      };
      await axios.put(`${API_BASE}/admin/settings/persona`, payload);
      toast.success(`AI personality updated to "${personaConfig.active_style}"`);
    } catch (error) {
      const msg = error.response?.data?.detail;
      if (Array.isArray(msg)) {
        toast.error(msg.map(e => e.msg).join(', '));
      } else {
        toast.error(msg || 'Failed to update persona');
      }
    } finally {
      setSavingPersona(false);
    }
  };

  const handleEvaluationSettingsSave = async () => {
    setSavingEvalSettings(true);
    try {
      await axios.put(`${API_BASE}/admin/settings/evaluation`, evalSettings);
      toast.success('Evaluation settings updated successfully');
    } catch (error) {
      console.error('Failed to update evaluation settings:', error);
      toast.error('Failed to update evaluation settings');
    } finally {
      setSavingEvalSettings(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_BASE}/admin/knowledge/documents`);
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to fetch documents');
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    
    // If API key is from env, we only need the model (no API key required)
    if (!settings.openrouter_api_key_from_env && !settings.openrouter_api_key.trim()) {
      toast.error('Please enter an OpenRouter API key');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_BASE}/admin/settings`, {
        openrouter_api_key: settings.openrouter_api_key,
        openrouter_model: settings.openrouter_model
      });
      toast.success('Settings updated successfully!');
      // Refresh settings to get updated values
      fetchSettings();
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const handleMigrateEmbeddings = async () => {
    setMigrating(true);
    setMigrationStatus(null);
    
    try {
      const response = await axios.post(`${API_BASE}/admin/migrate-embeddings`);
      setMigrationStatus(response.data);
      
      if (response.data.migrated > 0) {
        toast.success(`Successfully migrated ${response.data.migrated} documents!`);
      } else if (response.data.total === 0) {
        toast.info('No documents need migration');
      } else {
        toast.warning(`Migration completed but ${response.data.failed || 0} failed`);
      }
      
      // Refresh documents list
      fetchDocuments();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      toast.error(`Migration failed: ${errorMsg}`);
      setMigrationStatus({ error: errorMsg });
    } finally {
      setMigrating(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!uploadFile || !uploadTitle.trim()) {
      toast.error('Please select a file and enter a title');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('title', uploadTitle);

      const response = await axios.post(`${API_BASE}/admin/upload-document`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const chunks = response.data.chunks_created;
      toast.success(`Document uploaded: ${chunks} chunk${chunks !== 1 ? 's' : ''} created`);
      setUploadFile(null);
      setUploadTitle('');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (parentTitle, totalChunks) => {
    if (!window.confirm(`Delete "${parentTitle}" and all ${totalChunks} chunk${totalChunks !== 1 ? 's' : ''}? This cannot be undone.`)) {
      return;
    }
    try {
      await axios.delete(`${API_BASE}/admin/knowledge/documents/${encodeURIComponent(parentTitle)}`);
      toast.success(`Deleted "${parentTitle}" (${totalChunks} chunk${totalChunks !== 1 ? 's' : ''})`);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  const handleReplaceDocument = async (parentTitle, totalChunks) => {
    if (!window.confirm(`Replace "${parentTitle}"? This will delete the existing ${totalChunks} chunk${totalChunks !== 1 ? 's' : ''} first.`)) {
      return;
    }
    try {
      await axios.delete(`${API_BASE}/admin/knowledge/documents/${encodeURIComponent(parentTitle)}`);
      setUploadTitle(parentTitle);
      toast.info(`Deleted old "${parentTitle}". Now upload the replacement file.`);
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete old document');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (settingsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-500 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-4 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl lg:text-5xl font-bold font-display mb-4">
            <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Admin Panel
            </span>{' '}
            <span>⚙️</span>
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-6">
            Configure AI settings and manage the knowledge base
          </p>
          
          {/* Quick Actions */}
          <div className="flex justify-center space-x-4 flex-wrap gap-4">
            <Link
              to="/admin/llm-analytics"
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-lg hover:from-emerald-600 hover:to-teal-600 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              View LLM Analytics
            </Link>
            <Link
              to="/admin/memory-analytics"
              className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Brain className="w-4 h-4 mr-2" />
              Memory Analytics
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* OpenRouter Settings */}
          <Card className="glass border-0">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 font-display text-2xl text-slate-800">
                <Key className="w-6 h-6 text-emerald-600" />
                <span>OpenRouter Configuration</span>
              </CardTitle>
              <CardDescription>
                Configure your OpenRouter API key and preferred model for AI features
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSettingsSubmit} className="space-y-4" data-testid="openrouter-settings-form">
                <div className="space-y-2">
                  <Label htmlFor="api_key">OpenRouter API Key</Label>
                  <Input
                    id="api_key"
                    type="password"
                    placeholder={settings.openrouter_api_key_from_env ? "Configured in backend" : "sk-or-...your-api-key"}
                    value={settings.openrouter_api_key}
                    onChange={(e) => setSettings({...settings, openrouter_api_key: e.target.value})}
                    className="form-input"
                    disabled={settings.openrouter_api_key_from_env}
                    data-testid="api-key-input"
                  />
                  {settings.openrouter_api_key_from_env ? (
                    <p className="text-sm text-emerald-600">
                      ✓ API key is configured in backend environment variables (secure)
                    </p>
                  ) : (
                    <p className="text-sm text-slate-500">
                      Get your API key from{' '}
                      <a 
                        href="https://openrouter.ai/keys" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-emerald-600 hover:text-emerald-700 underline"
                      >
                        openrouter.ai/keys
                      </a>
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="model">Preferred Model</Label>
                  <Select
                    value={settings.openrouter_model}
                    onValueChange={(value) => setSettings({...settings, openrouter_model: value})}
                    data-testid="model-select"
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a model" />
                    </SelectTrigger>
                    <SelectContent>
                      {modelOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-slate-500">
                    This model will be used for milestone analysis, chat assistant, and report generation
                  </p>
                </div>

                <Button 
                  type="submit" 
                  disabled={loading}
                  className="w-full btn-hover gradient-emerald text-white"
                  data-testid="save-settings-button"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      {settings.openrouter_api_key_from_env ? 'Update Model' : 'Save Settings'}
                    </div>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Document Upload */}
          <Card className="glass border-0">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 font-display text-2xl text-slate-800">
                <Upload className="w-6 h-6 text-teal-600" />
                <span>Upload Knowledge Documents</span>
              </CardTitle>
              <CardDescription>
                Upload Word documents and text files to build the knowledge base
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleFileUpload} className="space-y-4" data-testid="document-upload-form">
                <div className="space-y-2">
                  <Label htmlFor="title">Document Title</Label>
                  <Input
                    id="title"
                    placeholder="e.g., Child Development Milestones"
                    value={uploadTitle}
                    onChange={(e) => setUploadTitle(e.target.value)}
                    className="form-input"
                    data-testid="document-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="file">Document File</Label>
                  <Input
                    id="file"
                    type="file"
                    accept=".txt,.docx,.xlsx"
                    onChange={(e) => setUploadFile(e.target.files[0])}
                    className="form-input"
                    data-testid="file-input"
                  />
                  <p className="text-sm text-slate-500">
                    Supported formats: .txt, .docx, .xlsx (max 10MB). Documents are automatically chunked and embedded.
                  </p>
                  {uploadFile && (
                    <div className="flex items-center space-x-2 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                      <FileText className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm text-emerald-700 font-medium">{uploadFile.name}</span>
                      <span className="text-xs text-emerald-600">
                        ({formatFileSize(uploadFile.size)})
                      </span>
                    </div>
                  )}
                </div>

                <Button 
                  type="submit" 
                  disabled={uploading}
                  className="w-full btn-hover gradient-teal text-white"
                  data-testid="upload-document-button"
                >
                  {uploading ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Uploading...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Upload Document
                    </div>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* System Prompt */}
        <Card className="glass border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 font-display text-2xl text-slate-800">
              <Settings className="w-6 h-6 text-emerald-600" />
              <span>System Prompt</span>
            </CardTitle>
            <CardDescription>
              The full instruction set sent to the AI before each conversation. Changes take effect within 60 seconds.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                className="form-input min-h-[300px] font-mono text-sm"
                rows={14}
                placeholder="Enter the system prompt instructions for the AI assistant..."
              />
              <p className="text-sm text-slate-500">
                This defines the AI's identity, rules, guardrails, and behavior. The tone selected below is appended automatically.
              </p>
              <Button
                onClick={handleSystemPromptSave}
                disabled={savingSystemPrompt}
                className="w-full btn-hover gradient-emerald text-white"
              >
                {savingSystemPrompt ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Save System Prompt
                  </div>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* AI Personality */}
        <Card className="glass border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 font-display text-2xl text-slate-800">
              <Palette className="w-6 h-6 text-violet-600" />
              <span>AI Personality</span>
            </CardTitle>
            <CardDescription>
              Choose the conversational tone. This is appended to the system prompt above.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Style selection */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {personaStyleOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setPersonaConfig({ ...personaConfig, active_style: option.value })}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      personaConfig.active_style === option.value
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-slate-200 bg-white/50 hover:border-slate-300'
                    }`}
                  >
                    <div className="font-semibold text-slate-800">{option.label}</div>
                    <p className="text-sm text-slate-500 mt-1">{option.description}</p>
                  </button>
                ))}
              </div>

              {/* Custom principles textarea */}
              {personaConfig.active_style === 'custom' && (
                <div className="space-y-2">
                  <Label htmlFor="custom_principles">Custom Personality Guidelines</Label>
                  <Textarea
                    id="custom_principles"
                    placeholder="Key principles:&#10;- Be supportive and encouraging&#10;- Use simple language&#10;- ..."
                    value={personaConfig.custom_principles}
                    onChange={(e) => setPersonaConfig({ ...personaConfig, custom_principles: e.target.value })}
                    className="form-input min-h-[120px]"
                    rows={6}
                  />
                  <p className="text-sm text-slate-500">
                    Write the personality guidelines the AI should follow. These replace the default "Key principles" block in the system prompt.
                  </p>
                </div>
              )}

              <Button
                onClick={handlePersonaSave}
                disabled={savingPersona}
                className="w-full btn-hover bg-gradient-to-r from-violet-500 to-purple-500 text-white"
              >
                {savingPersona ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Save Personality
                  </div>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Embedding Configuration */}
        <Card className="glass border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 font-display text-2xl text-slate-800">
              <Brain className="w-6 h-6 text-purple-600" />
              <span>Embedding Configuration</span>
            </CardTitle>
            <CardDescription>
              Configure semantic search and generate embeddings for existing documents
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* OpenAI API Key */}
              <div className="space-y-2">
                <Label htmlFor="openai_key">OpenAI API Key (for semantic search)</Label>
                <Input
                  id="openai_key"
                  type="password"
                  placeholder="sk-...your-openai-api-key"
                  value={embeddingSettings.openai_api_key}
                  onChange={(e) => setEmbeddingSettings({...embeddingSettings, openai_api_key: e.target.value})}
                  className="form-input"
                />
                <p className="text-sm text-slate-500">
                  Get your key from{' '}
                  <a 
                    href="https://platform.openai.com/api-keys" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-700 underline"
                  >
                    platform.openai.com/api-keys
                  </a>
                  {' '}(costs ~$0.00002 per query)
                </p>
              </div>

              {/* Semantic Search Toggle */}
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="use_embeddings"
                  checked={embeddingSettings.use_embeddings}
                  onChange={(e) => setEmbeddingSettings({...embeddingSettings, use_embeddings: e.target.checked})}
                  className="rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                />
                <Label htmlFor="use_embeddings">Enable semantic search with embeddings</Label>
              </div>

              {/* Migration Button */}
              <div className="pt-4 border-t border-slate-200">
                <p className="text-sm text-slate-600 mb-3">
                  Generate embeddings for existing documents that were uploaded before semantic search was enabled.
                </p>
                <Button 
                  type="button"
                  onClick={handleMigrateEmbeddings}
                  disabled={migrating}
                  className="w-full btn-hover bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                >
                  {migrating ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Migrating embeddings...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Brain className="w-4 h-4" />
                      Generate Embeddings for Existing Documents
                    </div>
                  )}
                </Button>
                
                {/* Migration Status */}
                {migrationStatus && (
                  <div className={`mt-3 p-3 rounded-lg ${
                    migrationStatus.error 
                      ? 'bg-red-50 border border-red-200' 
                      : 'bg-emerald-50 border border-emerald-200'
                  }`}>
                    <p className={`text-sm font-medium ${
                      migrationStatus.error ? 'text-red-700' : 'text-emerald-700'
                    }`}>
                      {migrationStatus.error 
                        ? `Error: ${migrationStatus.error}`
                        : migrationStatus.message || `Migrated ${migrationStatus.migrated} of ${migrationStatus.total} documents`
                      }
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Evaluation Settings */}
        <Card className="glass border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 font-display text-2xl text-slate-800">
              <Brain className="w-6 h-6 text-amber-600" />
              <span>LLM-as-Judge Evaluation Settings</span>
            </CardTitle>
            <CardDescription>
              Configure automated LLM evaluation system for AI responses
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="eval-enabled"
                  checked={evalSettings.llm_evaluation_enabled}
                  onChange={(e) => setEvalSettings({
                    ...evalSettings,
                    llm_evaluation_enabled: e.target.checked
                  })}
                  className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                />
                <Label htmlFor="eval-enabled">
                  Enable automated evaluations
                </Label>
              </div>

              <div className="space-y-2">
                <Label>Sample Rate</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    value={Math.round((evalSettings.llm_evaluation_sample_rate || 0) * 100)}
                    onChange={(e) => setEvalSettings({
                      ...evalSettings,
                      llm_evaluation_sample_rate: parseInt(e.target.value) / 100
                    })}
                    className="flex-1 accent-amber-500"
                  />
                  <span className="w-12 text-right font-mono text-sm font-medium">
                    {Math.round((evalSettings.llm_evaluation_sample_rate || 0) * 100)}%
                  </span>
                </div>
                <p className="text-sm text-slate-500">
                  % of messages to evaluate — 10% costs ~$90/month at 250K msgs; 0% disables
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Safety Threshold (0.0-1.0)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={evalSettings.llm_evaluation_threshold_safety}
                    onChange={(e) => setEvalSettings({
                      ...evalSettings,
                      llm_evaluation_threshold_safety: parseFloat(e.target.value)
                    })}
                    className="form-input"
                  />
                  <p className="text-sm text-slate-500">
                    Minimum safety score before flagging for review
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>General Threshold (0.0-1.0)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="1"
                    value={evalSettings.llm_evaluation_threshold_general}
                    onChange={(e) => setEvalSettings({
                      ...evalSettings,
                      llm_evaluation_threshold_general: parseFloat(e.target.value)
                    })}
                    className="form-input"
                  />
                  <p className="text-sm text-slate-500">
                    Minimum score for helpfulness, tone, accuracy
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Judge Model</Label>
                <Select
                  value={evalSettings.llm_evaluator_model}
                  onValueChange={(value) => setEvalSettings({
                    ...evalSettings,
                    llm_evaluator_model: value
                  })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a model" />
                  </SelectTrigger>
                  <SelectContent>
                    {modelOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-slate-500">
                  Model used for automated evaluation (via OpenRouter)
                </p>
              </div>

              <Button 
                onClick={handleEvaluationSettingsSave}
                disabled={savingEvalSettings}
                className="w-full btn-hover bg-gradient-to-r from-amber-500 to-orange-500 text-white"
              >
                {savingEvalSettings ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Save Evaluation Settings
                  </div>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Knowledge Base Documents */}
        <Card className="glass border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 font-display text-2xl text-slate-800">
              <Database className="w-6 h-6 text-cyan-600" />
              <span>Knowledge Base ({documents.length} documents)</span>
            </CardTitle>
            <CardDescription>
              Manage uploaded documents that power the AI assistant's knowledge
            </CardDescription>
          </CardHeader>
          <CardContent>
            {documents.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No documents uploaded yet</p>
                <p className="text-sm text-slate-400">
                  Upload your first document to start building the knowledge base
                </p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="documents-list">
                {documents.map((doc) => (
                  <div
                    key={doc.parent_title}
                    className="flex items-center justify-between p-4 bg-white/50 rounded-lg border border-emerald-200/50 hover:bg-white/70 transition-colors"
                    data-testid={`document-${doc.parent_title}`}
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-lg flex items-center justify-center">
                        <FileText className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-800">{doc.parent_title}</h3>
                        <p className="text-sm text-slate-600">
                          Uploaded on {new Date(doc.uploaded_at).toLocaleDateString()} •
                          {doc.file_type === 'xlsx' ? 'Excel' : doc.file_type === 'docx' ? 'Word' : 'Text'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                        {doc.total_chunks} chunk{doc.total_chunks !== 1 ? 's' : ''}
                      </span>
                      <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                        {(doc.total_chars / 1000).toFixed(1)}K chars
                      </span>
                      <button
                        onClick={() => handleReplaceDocument(doc.parent_title, doc.total_chunks)}
                        className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                        title="Replace document"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(doc.parent_title, doc.total_chunks)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete document"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Configuration Status */}
        <Card className="glass border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2 font-display text-2xl text-slate-800">
              <Sparkles className="w-6 h-6 text-amber-600" />
              <span>System Status</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-white/50 rounded-lg border border-emerald-200/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">OpenRouter API</span>
                  <div className={`w-3 h-3 rounded-full ${settings.openrouter_api_key_configured ? 'bg-green-500' : 'bg-red-500'}`}></div>
                </div>
                <p className="text-xs text-slate-500">
                  {settings.openrouter_api_key_configured ? 'Configured' : 'Not configured'}
                </p>
              </div>
              
              <div className="p-4 bg-white/50 rounded-lg border border-emerald-200/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">AI Model</span>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <p className="text-xs text-slate-500 truncate">
                  {settings.openrouter_model}
                </p>
              </div>
              
              <div className="p-4 bg-white/50 rounded-lg border border-emerald-200/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Knowledge Base</span>
                  <div className={`w-3 h-3 rounded-full ${documents.length > 0 ? 'bg-green-500' : 'bg-yellow-500'}`}></div>
                </div>
                <p className="text-xs text-slate-500">
                  {documents.length} documents
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPanel;
